import { useEffect, useState, useMemo, useRef } from "react";
import { X, Copy, Sparkles, Check, AlertCircle } from "lucide-react";
import { rgApi } from "@/lib/rgApi";
import { DECOR_ITEMS, BAINFLEURS_ITEMS, DECOR_TYPE_LABELS } from "./rgModules";
import { DEFAULT_CLEANING_PROTOCOL, mergeProtocolWithDefaults } from "./rgCleaningProtocol";

// Kit Couchage enfant — Lit parapluie (10 étapes, 3 sous-sections)
const CHILD_BED_PARAPLUIE = [
  { key: "cb_sortir_lit", label: "Sortir le lit parapluie", group: "Préparation du lit" },
  { key: "cb_ouvrir", label: "L'ouvrir", group: "Préparation du lit" },
  { key: "cb_aerer", label: "L'aérer", group: "Préparation du lit" },
  { key: "cb_aspirer", label: "L'aspirer", group: "Préparation du lit" },
  { key: "cb_surfaceuse", label: "Passer un coup de surfaceuse dessus", group: "Préparation du lit" },
  { key: "cb_verif_matelas", label: "Vérifier le matelas", group: "Préparation du matelas" },
  { key: "cb_antipoils_matelas", label: "Passer un coup anti-poils sur le matelas", group: "Préparation du matelas" },
  { key: "cb_alese", label: "1 alèse / drap-housse alèse", group: "Linge de lit à préparer" },
  { key: "cb_drap_housse", label: "1 drap-housse normal", group: "Linge de lit à préparer" },
  { key: "cb_couette_bebe", label: "1 petite couette bébé", group: "Linge de lit à préparer" },
];
/**
 * CleaningModal — Cockpit ménage jour J.
 *
 * Ordre de référence des blocs (Eva peut suivre un autre ordre selon les aléas) :
 *   Bloc 0 (buanderie, à l'avance)
 *   → Bloc ✨ (défroissage, à l'avance)
 *   → Bloc 1 (dégrossissage ~30min)
 *   → Bloc 2 (nettoyage complet + sols + bain ~45min)
 *   → Réfection du lit (~13-15min) + reminder chaussettes/serpillière
 *   → Finitions chambre + salle de bain (~15min)
 *   → Cuisine (~30min)
 *   → ✨ Dernier regard (~30min)
 *
 * L'ancienne liste "Préparation Jour J" (Linge prêt / Panier / Buanderie prête /
 * Petit-déj / 3e lit) a été déplacée dans Réglages (à imprimer, refonte à venir).
 */

const CLEAN_PROMPT = `Tu es mon assistant vocal pour le ménage du Curieux Refuge (Saint-Gilles-les-Bains, La Réunion).

CONTEXTE :
- Refuge : location courte durée avec ambiance zen, végétale, naturelle.
- Type de séjour : couple ou petit groupe (parfois avec enfants ou 3e voyageur).
- Ambiance : soin du détail, minutieux, esthétique organique.

MISSION :
Guide-moi à voix haute étape par étape pour :
1. Faire le ménage complet du refuge après un départ voyageur
2. Préparer chaque zone (salle de bain, chambre, cuisine, terrasse, bassin)
3. Vérifier les stocks de produits et le linge propre
4. Anticiper la préparation des extras (petit déjeuner, bain de fleurs, décor)
5. Me rappeler les petits détails d'accueil (bougie, mot de bienvenue, musique douce)

STYLE :
- Voix calme, bienveillante, précise.
- Une seule tâche à la fois, avec confirmation avant de passer à la suivante.
- Rappelle les bons gestes écolos (vinaigre, savon noir, éviter les produits chimiques).

Commençons ensemble. Dis-moi juste "PRÊT" quand tu as commencé, et guide-moi.`;

// Définition des blocs — chargée dynamiquement depuis /api/rg/cleaning-protocol
// Défaut fourni par DEFAULT_CLEANING_PROTOCOL (rgCleaningProtocol.js).
// La logique de compteur et de sessions s'appuie sur des identifiants stables
// (bloc.key, item.key) préservés lors des réorganisations Réglages.

export default function CleaningModal({ open, onClose, reservation, cleaningId, done, onDone, onUpdateReservation }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({}); // {bloc_key: {target_sec, last, best, history}}
  const [protocol, setProtocol] = useState(DEFAULT_CLEANING_PROTOCOL);
  // État LOCAL des coches — mise à jour optimiste (réaction immédiate au 1er appui,
  // sans recharger tout le Cockpit). Persistance en arrière-plan, revert si échec.
  const [localCp, setLocalCp] = useState(reservation?.cleaning_prep || {});
  const cpRef = useRef(reservation?.cleaning_prep || {});
  const [saveErr, setSaveErr] = useState(false);

  useEffect(() => { if (!open) setCopied(false); }, [open]);

  // (Re)synchronise les coches depuis le serveur uniquement à l'ouverture / changement de résa
  useEffect(() => {
    if (open) {
      const sv = reservation?.cleaning_prep || {};
      setLocalCp(sv);
      cpRef.current = sv;
      setSaveErr(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reservation?.id]);

  useEffect(() => {
    if (!open) return;
    rgApi.cleaningStats().then((d) => setStats(d.blocs || {})).catch(() => {});
    rgApi.getCleaningProtocol()
      .then((d) => {
        if (d?.using_defaults || !d?.blocs?.length) setProtocol(DEFAULT_CLEANING_PROTOCOL);
        else setProtocol(mergeProtocolWithDefaults(d));
      })
      .catch(() => setProtocol(DEFAULT_CLEANING_PROTOCOL));
  }, [open]);

  // Filtre les items masqués — seuls les items visibles comptent pour le compteur X/Y.
  const BLOCS = useMemo(() => {
    return (protocol.blocs || []).map((b) => ({
      ...b,
      items: (b.items || []).filter((it) => !it.hidden),
    }));
  }, [protocol]);

  if (!open || !reservation) return null;
  const r = reservation;
  const cp = localCp;
  const sessions = r.cleaning_sessions || {};

  // Persistance optimiste : met à jour l'UI tout de suite, enregistre en arrière-plan.
  const persistCp = (next) => {
    cpRef.current = next;
    setLocalCp(next);
    setSaveErr(false);
    Promise.resolve(onUpdateReservation({ cleaning_prep: next })).catch(() => setSaveErr(true));
  };

  const toggle = (key) => {
    persistCp({ ...cpRef.current, [key]: !cpRef.current[key] });
  };

  // Toggle d'un item matériel pour un bloc additionnel (décor / bain de fleurs)
  const toggleMaterial = (materialField, itemKey) => {
    const cur = cpRef.current[materialField] || {};
    persistCp({ ...cpRef.current, [materialField]: { ...cur, [itemKey]: !cur[itemKey] } });
  };

  const saveSession = async (blocKey, patch) => {
    setBusy(true);
    try {
      const prev = sessions[blocKey] || {};
      const body = {
        start_at: patch.start_at !== undefined ? patch.start_at : (prev.start_at || null),
        end_at: patch.end_at !== undefined ? patch.end_at : (prev.end_at || null),
      };
      const res = await rgApi.saveCleaningSession(r.id, blocKey, body);
      // Rafraîchit la réservation via callback parent (récupère cleaning_sessions à jour)
      await onUpdateReservation({});
      if (res?.stats) setStats(res.stats);
    } finally { setBusy(false); }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(CLEAN_PROMPT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copiez le prompt :", CLEAN_PROMPT);
    }
  };

  const launchAssistant = () => {
    window.open("https://chat.openai.com/", "_blank", "noopener,noreferrer");
  };

  const toggleDone = async () => {
    if (!cleaningId) return;
    setBusy(true);
    try {
      await rgApi.setCompletion(cleaningId, !done);
      if (onDone) await onDone();
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="rg-modal-back" onClick={onClose} data-testid="rg-cleaning-back" />
      <div className="rg-modal" role="dialog" aria-modal="true" data-testid="rg-cleaning-modal">
        <div className="rg-modal-head">
          <div>
            <div className="rg-tiny">🧹 Ménage · {r.name}</div>
            <div className="rg-modal-title">Cockpit ménage jour J</div>
          </div>
          <button className="rg-icon-btn" onClick={onClose} data-testid="rg-cleaning-close"><X size={18} /></button>
        </div>

        <div className="rg-modal-body">
          {saveErr && (
            <div className="rg-alert-warn" style={{ marginBottom: 10, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }} data-testid="rg-cleaning-save-error">
              <AlertCircle size={14} />
              <span style={{ fontSize: 12 }}>Enregistrement de la coche échoué — réessaie en re-cochant.</span>
            </div>
          )}
          {BLOCS.map((bloc) => (
            <BlocSection
              key={bloc.key}
              bloc={bloc}
              cp={cp}
              busy={busy}
              onToggle={toggle}
              session={sessions[bloc.key] || {}}
              stat={stats[bloc.key] || null}
              onSaveSession={saveSession}
            />
          ))}

          {/* 🌹 Bloc conditionnel Déco du Refuge */}
          {(r.extras || []).includes("decor") || ["nuit_rouge", "nuit_rose"].includes(r.decor_type) ? (
            <ExtraBloc
              blocKey="decor"
              title={`🌹 Déco du Refuge${r.decor_type ? ` — ${DECOR_TYPE_LABELS[r.decor_type] || ""}` : ""}`}
              subtitle="Bloc conditionnel — matériel + installation"
              items={DECOR_ITEMS}
              decorType={r.decor_type}
              materialField="decor_material_ready"
              cp={cp}
              busy={busy}
              onToggleMaterial={toggleMaterial}
              session={sessions.decor || {}}
              stat={stats.decor || null}
              onSaveSession={saveSession}
            />
          ) : null}

          {/* 🌸 Bloc conditionnel Bain de fleurs */}
          {(r.extras || []).includes("bain_fleurs") ? (
            <ExtraBloc
              blocKey="bain_fleurs"
              title="🌸 Bain de fleurs"
              subtitle="Bloc conditionnel — matériel + installation"
              items={BAINFLEURS_ITEMS}
              decorType={null}
              materialField="bainfleurs_material_ready"
              cp={cp}
              busy={busy}
              onToggleMaterial={toggleMaterial}
              session={sessions.bain_fleurs || {}}
              stat={stats.bain_fleurs || null}
              onSaveSession={saveSession}
            />
          ) : null}

          {/* 🛏️ Bloc conditionnel Couchage enfant */}
          {(() => {
            const t = r.troisieme_lit_type;
            const cat = r.third_guest_category;
            const cbType = (t === "parapluie" || t === "appoint")
              ? t
              : (cat === "enfant" || cat === "bebe") ? "parapluie" : null;
            if (!cbType) return null;
            return (
              <ChildBedBloc
                cbType={cbType}
                cp={cp}
                busy={busy}
                onToggleMaterial={toggleMaterial}
                stat={stats.child_bed || null}
              />
            );
          })()}

          <hr className="rg-modal-sep" />

          {/* Assistant IA */}
          <div className="rg-modal-section" data-testid="rg-assistant-section">
            <h4 className="rg-modal-subtitle">Assistant Ménage</h4>
            <p className="rg-muted" style={{ marginTop: 0 }}>
              Copie le prompt puis lance ChatGPT en mode vocal pour être guidé pas à pas.
            </p>
            <div className="rg-row" style={{ gap: 8, flexWrap: "wrap", marginTop: 8 }}>
              <button className="rg-btn ghost" onClick={copyPrompt} data-testid="rg-cleaning-copy">
                <Copy size={13} /> {copied ? "Copié ✓" : "Copier le prompt"}
              </button>
              <button className="rg-btn" onClick={launchAssistant} data-testid="rg-cleaning-launch">
                <Sparkles size={13} /> Lancer l&apos;assistant
              </button>
            </div>
            {cleaningId && (
              <button
                className={`rg-btn ${done ? "done" : "warn"}`}
                style={{ marginTop: 12, width: "100%", justifyContent: "center" }}
                onClick={toggleDone}
                disabled={busy}
                data-testid="rg-cleaning-toggle-done"
              >
                <Check size={13} /> {done ? "Ménage terminé ✓" : "Marquer terminé"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function fmtDurMin(sec) {
  if (!sec || sec <= 0) return "—";
  const total = Math.round(sec);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m > 0 && s > 0) return `${m} min ${String(s).padStart(2, "0")} s`;
  if (m > 0) return `${m} min`;
  return `${s} s`;
}

function fmtTarget(sec) {
  if (!sec || sec <= 0) return "";
  const m = Math.round(sec / 60);
  return `≈ ${m} min`;
}

function BlocSection({ bloc, cp, busy, onToggle, session, stat, onSaveSession }) {
  const doneCount = bloc.items.filter((it) => cp[it.key]).length;
  const total = bloc.items.length;
  const allDone = doneCount === total && total > 0;
  const finished = !!(session && session.end_at); // bloc terminé = heure de fin saisie

  const [startVal, setStartVal] = useState(session?.start_at || "");
  const [endVal, setEndVal] = useState(session?.end_at || "");

  useEffect(() => {
    setStartVal(session?.start_at || "");
    setEndVal(session?.end_at || "");
  }, [session?.start_at, session?.end_at]);

  const commitStart = () => {
    if ((session?.start_at || "") !== startVal) onSaveSession(bloc.key, { start_at: startVal || null });
  };
  const commitEnd = () => {
    if ((session?.end_at || "") !== endVal) onSaveSession(bloc.key, { end_at: endVal || null });
  };

  const targetSec = stat?.target_sec || 0;
  const recordSec = stat?.best?.duration_sec || 0;
  const lastSec = stat?.last?.duration_sec || 0;
  const durationSec = session?.duration_sec || 0;

  return (
    <div className="rg-modal-section" data-testid={`rg-bloc-${bloc.key}`}>
      <div className="rg-row" style={{ justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h4 className="rg-modal-subtitle" style={{ margin: 0 }}>{bloc.title}</h4>
          {bloc.subtitle && (
            <div className="rg-tiny" style={{ color: "var(--rg-text-3)", marginTop: 2 }}>
              {bloc.subtitle}{targetSec ? ` · Cible ${fmtTarget(targetSec)}` : ""}
              {recordSec ? ` · 🏆 Record ${fmtDurMin(recordSec)}` : ""}
            </div>
          )}
        </div>
        <span
          className="rg-tiny"
          style={{ color: finished ? "var(--rg-success)" : "var(--rg-text-2)", whiteSpace: "nowrap" }}
          data-testid={`rg-bloc-${bloc.key}-count`}
        >
          {doneCount}/{total}{finished ? " · terminé ✓" : ""}
        </span>
      </div>

      {finished && (
        <div className="rg-prog-done" style={{ marginTop: 6 }} data-testid={`rg-bloc-${bloc.key}-finished`}>
          ✅ Bloc terminé{session?.duration_sec ? ` · ${fmtDurMin(session.duration_sec)}` : ""}
        </div>
      )}

      {/* Chrono */}
      <div
        style={{
          display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
          marginTop: 8, marginBottom: 6, fontSize: 12,
        }}
        data-testid={`rg-bloc-${bloc.key}-chrono`}
      >
        <span className="rg-tiny" style={{ color: "var(--rg-text-3)" }}>⏱ Début</span>
        <input
          type="time"
          value={startVal}
          onChange={(e) => setStartVal(e.target.value)}
          onBlur={commitStart}
          disabled={busy}
          className="rg-input"
          style={{ width: 90, padding: "4px 8px", fontSize: 12 }}
          data-testid={`rg-bloc-${bloc.key}-start`}
        />
        <span className="rg-tiny" style={{ color: "var(--rg-text-3)" }}>Fin</span>
        <input
          type="time"
          value={endVal}
          onChange={(e) => setEndVal(e.target.value)}
          onBlur={commitEnd}
          disabled={busy}
          className="rg-input"
          style={{ width: 90, padding: "4px 8px", fontSize: 12 }}
          data-testid={`rg-bloc-${bloc.key}-end`}
        />
        {durationSec > 0 && (
          <span
            data-testid={`rg-bloc-${bloc.key}-duration`}
            style={{ color: "var(--rg-success)", fontWeight: 600 }}
          >
            ⇒ {fmtDurMin(durationSec)}
          </span>
        )}
        {!durationSec && lastSec > 0 && (
          <span className="rg-tiny" style={{ color: "var(--rg-text-3)" }}>
            Dernière : {fmtDurMin(lastSec)}
          </span>
        )}
      </div>

      {bloc.banner && (
        <div
          className="rg-prog-done"
          style={{ background: "var(--rg-primary-light)", color: "var(--rg-primary)", marginTop: 6 }}
          data-testid={`rg-bloc-${bloc.key}-banner`}
        >
          💡 {bloc.banner}
        </div>
      )}
      {allDone && bloc.successMsg && (
        <div className="rg-prog-done" data-testid={`rg-bloc-${bloc.key}-avance`}>
          ✨ {bloc.successMsg}
        </div>
      )}
      {bloc.items.map((it) => (
        <div key={it.key} className={`rg-prep-row ${cp[it.key] ? "done" : ""}`} data-testid={`rg-prep-${it.key}`}>
          <label className="rg-prep-check" title={it.tip || ""}>
            <input
              type="checkbox"
              checked={!!cp[it.key]}
              onChange={() => onToggle(it.key)}
              data-testid={`rg-prep-toggle-${it.key}`}
            />
            <span className="rg-prep-label">{it.label}</span>
          </label>
        </div>
      ))}
    </div>
  );
}


// ExtraBloc — bloc conditionnel Décor / Bain de fleurs
// Structure : Préparation du matériel (checklist réutilisée) + Installation (chrono)
function ExtraBloc({
  blocKey, title, subtitle, items, decorType, materialField,
  cp, busy, onToggleMaterial, session, stat, onSaveSession,
}) {
  const mr = cp[materialField] || {};
  // Filtre les items visibles (roses_decor a une buyLabel dépendante du decor_type mais reste visible)
  const visible = items.filter((it) => !it.visible || it.visible({}, decorType));
  const materialCount = visible.filter((it) => mr[it.key]).length;
  const materialAll = materialCount === visible.length && visible.length > 0;
  const installDone = !!(session && session.end_at);
  const allDone = materialAll && installDone;

  const targetSec = stat?.target_sec || 0;
  const recordSec = stat?.best?.duration_sec || 0;
  const lastSec = stat?.last?.duration_sec || 0;
  const durationSec = session?.duration_sec || 0;

  const [startVal, setStartVal] = useState(session?.start_at || "");
  const [endVal, setEndVal] = useState(session?.end_at || "");
  useEffect(() => {
    setStartVal(session?.start_at || "");
    setEndVal(session?.end_at || "");
  }, [session?.start_at, session?.end_at]);
  const commitStart = () => { if ((session?.start_at || "") !== startVal) onSaveSession(blocKey, { start_at: startVal || null }); };
  const commitEnd = () => { if ((session?.end_at || "") !== endVal) onSaveSession(blocKey, { end_at: endVal || null }); };

  return (
    <div className="rg-modal-section" data-testid={`rg-bloc-${blocKey}`}>
      <div className="rg-row" style={{ justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h4 className="rg-modal-subtitle" style={{ margin: 0 }}>{title}</h4>
          <div className="rg-tiny" style={{ color: "var(--rg-text-3)", marginTop: 2 }}>
            {subtitle}
            {targetSec ? ` · Cible ≈ ${Math.round(targetSec / 60)} min` : ""}
            {recordSec ? ` · 🏆 Record ${Math.round(recordSec / 60)} min` : ""}
          </div>
        </div>
        <span
          className="rg-tiny"
          style={{ color: allDone ? "var(--rg-success)" : "var(--rg-text-2)", whiteSpace: "nowrap" }}
          data-testid={`rg-bloc-${blocKey}-count`}
        >
          {materialCount}/{visible.length} matériel · {installDone ? "installation ✓" : "installation ⏳"}
        </span>
      </div>

      {/* Sous-partie 1 : Préparation du matériel */}
      <div style={{ marginTop: 10, marginBottom: 8, fontSize: 12, fontWeight: 600, color: "var(--rg-text-2)" }}>
        1. Préparation du matériel
      </div>
      {materialAll && (
        <div className="rg-prog-done" data-testid={`rg-bloc-${blocKey}-material-done`}>
          ✅ Matériel rassemblé
        </div>
      )}
      {visible.map((it) => {
        const label = typeof it.buyLabel === "function" ? it.buyLabel({}, decorType) : it.label;
        return (
          <div key={it.key} className={`rg-prep-row ${mr[it.key] ? "done" : ""}`} data-testid={`rg-mat-${blocKey}-${it.key}`}>
            <label className="rg-prep-check">
              <input
                type="checkbox"
                checked={!!mr[it.key]}
                onChange={() => onToggleMaterial(materialField, it.key)}
                data-testid={`rg-mat-toggle-${blocKey}-${it.key}`}
              />
              <span className="rg-prep-label">{it.icon} {label}</span>
            </label>
          </div>
        );
      })}

      {/* Sous-partie 2 : Installation */}
      <div style={{ marginTop: 12, marginBottom: 6, fontSize: 12, fontWeight: 600, color: "var(--rg-text-2)" }}>
        2. Installation
      </div>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", fontSize: 12 }}
        data-testid={`rg-bloc-${blocKey}-chrono`}
      >
        <span className="rg-tiny" style={{ color: "var(--rg-text-3)" }}>⏱ Début</span>
        <input
          type="time"
          value={startVal}
          onChange={(e) => setStartVal(e.target.value)}
          onBlur={commitStart}
          disabled={busy}
          className="rg-input"
          style={{ width: 90, padding: "4px 8px", fontSize: 12 }}
          data-testid={`rg-bloc-${blocKey}-start`}
        />
        <span className="rg-tiny" style={{ color: "var(--rg-text-3)" }}>Fin</span>
        <input
          type="time"
          value={endVal}
          onChange={(e) => setEndVal(e.target.value)}
          onBlur={commitEnd}
          disabled={busy}
          className="rg-input"
          style={{ width: 90, padding: "4px 8px", fontSize: 12 }}
          data-testid={`rg-bloc-${blocKey}-end`}
        />
        {durationSec > 0 && (
          <span data-testid={`rg-bloc-${blocKey}-duration`} style={{ color: "var(--rg-success)", fontWeight: 600 }}>
            ⇒ {Math.round(durationSec / 60)} min
          </span>
        )}
        {!durationSec && lastSec > 0 && (
          <span className="rg-tiny" style={{ color: "var(--rg-text-3)" }}>
            Dernière : {Math.round(lastSec / 60)} min
          </span>
        )}
      </div>
      {installDone && (
        <div className="rg-prog-done" style={{ marginTop: 8 }} data-testid={`rg-bloc-${blocKey}-install-done`}>
          ✅ Installation terminée
        </div>
      )}
    </div>
  );
}

// ChildBedBloc — bloc conditionnel Couchage enfant (checklist seule, pas de chrono)
function ChildBedBloc({ cbType, cp, busy, onToggleMaterial, stat }) {
  const items = cbType === "parapluie" ? CHILD_BED_PARAPLUIE : [];
  const mr = cp.child_bed_ready || {};
  const doneCount = items.filter((it) => mr[it.key]).length;
  const total = items.length;
  const allDone = total > 0 && doneCount === total;
  const targetSec = stat?.target_sec || 0;

  const typeLabel = cbType === "parapluie" ? "Lit parapluie" : "Fauteuil pliable";

  // Regroupement par sous-section
  const groups = items.reduce((acc, it) => {
    (acc[it.group] = acc[it.group] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="rg-modal-section" data-testid="rg-bloc-child_bed">
      <div className="rg-row" style={{ justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <h4 className="rg-modal-subtitle" style={{ margin: 0 }}>🛏️ Couchage enfant — {typeLabel}</h4>
          <div className="rg-tiny" style={{ color: "var(--rg-text-3)", marginTop: 2 }}>
            Bloc conditionnel
            {targetSec ? ` · Cible ≈ ${Math.round(targetSec / 60)} min` : ""}
          </div>
        </div>
        <span
          className="rg-tiny"
          style={{ color: allDone ? "var(--rg-success)" : "var(--rg-text-2)", whiteSpace: "nowrap" }}
          data-testid="rg-bloc-child_bed-count"
        >
          {doneCount}/{total}{allDone ? " ✓" : ""}
        </span>
      </div>

      {allDone && (
        <div className="rg-prog-done" data-testid="rg-bloc-child_bed-done">
          ✅ Couchage enfant prêt
        </div>
      )}

      {total === 0 && (
        <div className="rg-alert-warn" style={{ marginTop: 8 }}>
          <div className="rg-alert-title">Checklist Fauteuil pliable à définir</div>
          <p className="rg-muted" style={{ margin: "6px 0 0", fontSize: 12 }}>
            La liste des étapes pour préparer le fauteuil pliable sera fournie prochainement.
          </p>
        </div>
      )}

      {Object.entries(groups).map(([group, list]) => (
        <div key={group} style={{ marginTop: 10 }}>
          <div style={{ marginBottom: 6, fontSize: 12, fontWeight: 600, color: "var(--rg-text-2)" }}>
            {group}
          </div>
          {list.map((it) => (
            <div key={it.key} className={`rg-prep-row ${mr[it.key] ? "done" : ""}`} data-testid={`rg-cb-${it.key}`}>
              <label className="rg-prep-check">
                <input
                  type="checkbox"
                  checked={!!mr[it.key]}
                  onChange={() => onToggleMaterial("child_bed_ready", it.key)}
                  data-testid={`rg-cb-toggle-${it.key}`}
                />
                <span className="rg-prep-label">{it.label}</span>
              </label>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

